import React, { useState } from "react";
import { IconButton, Tooltip, Snackbar, Alert, Slide, Grid, Box, Container, useMediaQuery, useTheme } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CodeIcon from "@mui/icons-material/Code";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import AnseraLogo from "assets/images/Anser.png";

const Script = () => {
  const [copied, setCopied] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const scriptText = `<script src="https://ansera-cdn.s3.us-east-1.amazonaws.com/generic/embed.js"></script>
<script>
  window.ansera_domain_data = {
    "domainName": "${sessionStorage.getItem("domain_name")}",
    "domainToken": "${sessionStorage.getItem("domainId")}"
  };
</script>`;

  const handleCopy = () => {
    navigator.clipboard.writeText(scriptText).then(() => {
      setCopied(true);
      setSnackbarOpen(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const source = sessionStorage.getItem("source");
  
  const getLoomEmbedUrl = (shareUrl) => {
    return shareUrl.replace('/share/', '/embed/');
  };

  const loomShareUrls = {
    wix: "https://www.loom.com/share/b11c4b0ae4e948a187fb70b6a88409ad", 
    squarespace: "https://www.loom.com/share/c30d6e3bd9604382830897b8416827d5",
  };

  const shouldShowVideo = source === "wix" || source === "squarespace";
  const loomEmbedUrl = shouldShowVideo ? getLoomEmbedUrl(loomShareUrls[source]) : null;

  const steps = source === "wix" ? [
    "Log in to your Wix account and open your website dashboard",
    "Click on <b>Settings</b> in the left menu",
    "Select <b>Custom Code</b> under the <b>Development and integrations</b> section",
    "Click <b>Add Code</b> in <b>Body-End</b> section and paste the script",
    "Select <b>Add code</b> to all pages and choose Load code once",
    "Save and publish your site"
  ] : [
    "Log in to your Squarespace account and go to your site",
    "Click on <b>Edit</b> button on the top left corner",
    "Scroll down to site footer and click on <b>Edit Site Footer</b>",
    "Click <b>Add Block</b> and select <b></> Code",
    "Paste the script into the <b>Footer</b> section",
    "Save your changes",
    "The script will be applied to all pages of your site"
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          icon={<CheckCircleIcon fontSize="inherit" />}
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            color: "#ffffff",
            backgroundColor: "#008080",
            boxShadow: "0px 8px 24px rgba(0, 128, 128, 0.3)",
            borderRadius: "12px",
          }}
        >
          Script copied to clipboard!
        </Alert>
      </Snackbar>

      <SoftBox display="flex" flexDirection="column" gap={{ xs: 2, sm: 3, md: 4 }}>
        <SoftBox
          sx={{
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: { xs: "12px", sm: "16px", md: "20px" },
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(0, 0, 0, 0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.12)",
            }
          }}
          display="flex"
          flexDirection="column"
          gap={{ xs: 2, sm: 2.5, md: 3 }}
        >
          <SoftBox display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: "#f0f9f9",
                borderRadius: "12px",
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img src={AnseraLogo} alt="Ansera Logo" width={isMobile ? 24 : 30} />
            </Box>
            <SoftTypography 
              variant={isMobile ? "h6" : "h5"} 
              fontWeight="bold"
              sx={{
                background: "linear-gradient(135deg, #219297 0%, #008080 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Installation Script
            </SoftTypography>
          </SoftBox>
          <SoftBox
            sx={{
              backgroundColor: "#f0f9f9",
              borderLeft: "4px solid #219297",
              borderRadius: "8px",
              p: { xs: 1.5, sm: 2 },
            }}
          >
            <SoftBox display="flex" alignItems="flex-start" gap={1}>
              <CodeIcon sx={{ color: "#219297", fontSize: 20, mt: 0.3, flexShrink: 0 }} />
              <SoftTypography variant="body2" sx={{ fontSize: { xs: "0.813rem", sm: "0.875rem" } }}>
                <b>Important:</b> Add this script to your site's footer (just before the closing {`</body>`} tag) to access all features and settings.
              </SoftTypography>
            </SoftBox>
          </SoftBox>

          <SoftBox
            sx={{
              backgroundColor: "#1e1e1e",
              border: "2px solid #2d2d2d",
              borderRadius: "12px",
              p: { xs: 2, sm: 2.5 },
              position: "relative",
              overflow: "auto",
              maxHeight: { xs: "200px", sm: "none" },
            }}
          >
            <SoftTypography 
              variant="body2" 
              sx={{ 
                whiteSpace: "pre-wrap",
                color: "#e0e0e0",
                fontFamily: "'Fira Code', 'Courier New', monospace",
                fontSize: { xs: "0.75rem", sm: "0.813rem", md: "0.875rem" },
                lineHeight: 1.6,
              }}
            >
              {scriptText}
            </SoftTypography>

            <Tooltip title={copied ? "Copied!" : "Copy to clipboard"} placement="left">
              <IconButton
                onClick={handleCopy}
                sx={{
                  position: "absolute",
                  top: { xs: 8, sm: 12 },
                  right: { xs: 8, sm: 12 },
                  backgroundColor: copied ? "#4caf50" : "#219297",
                  color: "#fff",
                  "&:hover": { 
                    backgroundColor: copied ? "#45a049" : "#1a767a",
                    transform: "scale(1.05)",
                  },
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  transition: "all 0.3s ease",
                  boxShadow: "0px 4px 12px rgba(33, 146, 151, 0.3)",
                }}
              >
                {copied ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </SoftBox>
        </SoftBox>

        {shouldShowVideo && (
          <SoftBox
            sx={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: { xs: "12px", sm: "16px", md: "20px" },
              boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.08)",
              border: "1px solid rgba(0, 0, 0, 0.05)",
              transition: "all 0.3s ease",
              "&:hover": {
                boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.12)",
              }
            }}
          >
            <SoftBox mb={{ xs: 2, sm: 3 }}>
              <SoftTypography 
                variant={isMobile ? "h6" : "h5"} 
                fontWeight="bold"
                sx={{
                  background: "linear-gradient(135deg, #219297 0%, #008080 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Installation Guide
              </SoftTypography>
              <SoftTypography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: { xs: "0.813rem", sm: "0.875rem" } }}>
                Follow these steps to integrate the script into your {source === "wix" ? "Wix" : "Squarespace"} website
              </SoftTypography>
            </SoftBox>
            
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
              <Grid item xs={12} md={6}>
                <SoftBox display="flex" flexDirection="column" gap={{ xs: 2, sm: 2.5 }}>
                  <SoftBox
                    sx={{
                      backgroundColor: "#f0f9f9",
                      borderRadius: "12px",
                      p: { xs: 1.5, sm: 2 },
                      border: "2px solid #219297",
                    }}
                  >
                    <SoftTypography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      sx={{ 
                        color: "#219297",
                        fontWeight: "bold",
                      }}
                    >
                      For {source === "wix" ? "Wix" : "Squarespace"} Websites
                    </SoftTypography>
                  </SoftBox>
                  
                  <SoftBox display="flex" flexDirection="column" gap={{ xs: 1.5, sm: 2 }}>
                    {steps.map((step, index) => (
                      <SoftBox
                        key={index}
                        sx={{
                          display: "flex",
                          gap: { xs: 1.5, sm: 2 },
                          alignItems: "flex-start",
                          p: { xs: 1.5, sm: 2 },
                          backgroundColor: "#ffffff",
                          borderRadius: "10px",
                          border: "1px solid #e0e0e0",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            backgroundColor: "#f8f9fa",
                            borderColor: "#219297",
                            transform: "translateX(4px)",
                            boxShadow: "0px 4px 12px rgba(33, 146, 151, 0.1)",
                          }
                        }}
                      >
                        <Box
                          sx={{
                            minWidth: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                            borderRadius: "50%",
                            backgroundColor: "#219297",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <SoftTypography 
                          variant="body2" 
                          sx={{ 
                            flex: 1,
                            fontSize: { xs: "0.813rem", sm: "0.875rem" },
                            lineHeight: 1.6,
                          }}
                          dangerouslySetInnerHTML={{ 
                            __html: step.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>') 
                          }}
                        />
                      </SoftBox>
                    ))}
                  </SoftBox>
                  
                  <SoftBox 
                    sx={{
                      mt: { xs: 1, sm: 2 },
                      p: { xs: 1.5, sm: 2 },
                      backgroundColor: "#e0fffaff",
                      borderRadius: "10px",
                      border: "1px solid #45d1d6ff",
                    }}
                  >
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <PlayCircleOutlineIcon sx={{ color: "#00b8b8ff", fontSize: { xs: 20, sm: 24 } }} />
                      <SoftTypography 
                        variant="body2" 
                        sx={{ 
                          fontStyle: "italic", 
                          color: "#02bbc9ff",
                          fontSize: { xs: "0.813rem", sm: "0.875rem" }
                        }}
                      >
                        {isTablet ? "Watch the video below for a visual guide" : "Need help? Watch the video demonstration"}
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftBox
                  sx={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
                    border: "2px solid #e0e0e0",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.2)",
                      borderColor: "#219297",
                    }
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      paddingBottom: "62.5%",
                      height: 0,
                      backgroundColor: "#000",
                    }}
                  >
                    <iframe
                      src={loomEmbedUrl}
                      frameBorder="0"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                    ></iframe>
                  </div>
                </SoftBox>
                
                <SoftBox 
                  mt={{ xs: 1, sm: 1.5 }}
                  sx={{
                    p: { xs: 1, sm: 1.5 },
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                  }}
                >
                  <SoftTypography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      display: "block",
                      fontSize: { xs: "0.75rem", sm: "0.813rem" }
                    }}
                  >
                    Complete video walkthrough of the installation process
                  </SoftTypography>
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
        )}
      </SoftBox>
    </Container>
  );
};

export default Script;